"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";

type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  reducedMotion: boolean;
  highContrast: boolean;
  mounted: boolean;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
  setReducedMotion: (v: boolean) => void;
  setHighContrast: (v: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getSystemTheme(): Theme {
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

function getSystemReducedMotion(): boolean {
  if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return true;
  }
  return false;
}

function applyTheme(t: Theme) {
  document.documentElement.setAttribute("data-theme", t);
}

function applyReducedMotion(v: boolean) {
  document.documentElement.setAttribute("data-reduce-motion", String(v));
}

function applyHighContrast(v: boolean) {
  document.documentElement.setAttribute("data-high-contrast", String(v));
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("light");
  const [reducedMotion, setReducedMotionState] = useState(false);
  const [highContrast, setHighContrastState] = useState(false);
  const [mounted, setMounted] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect -- hydrating from localStorage/system preferences on mount */
  useEffect(() => {
    setMounted(true);

    const storedTheme = localStorage.getItem("theme");
    if (storedTheme === "light" || storedTheme === "dark") {
      setThemeState(storedTheme);
      applyTheme(storedTheme);
    } else {
      const detected = getSystemTheme();
      setThemeState(detected);
      applyTheme(detected);
    }

    const storedRM = localStorage.getItem("reducedMotion");
    if (storedRM === "true") {
      setReducedMotionState(true);
      applyReducedMotion(true);
    } else if (storedRM === "false") {
      setReducedMotionState(false);
      applyReducedMotion(false);
    } else {
      const systemRM = getSystemReducedMotion();
      setReducedMotionState(systemRM);
      applyReducedMotion(systemRM);
    }

    const storedHC = localStorage.getItem("highContrast");
    if (storedHC === "true") {
      setHighContrastState(true);
      applyHighContrast(true);
    }

    const colorSchemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleColorSchemeChange = () => {
      const current = localStorage.getItem("theme");
      if (!current || (current !== "light" && current !== "dark")) {
        const newTheme = getSystemTheme();
        setThemeState(newTheme);
        applyTheme(newTheme);
      }
    };
    colorSchemeQuery.addEventListener("change", handleColorSchemeChange);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "theme" && e.newValue) {
        const newTheme: Theme = e.newValue === "dark" ? "dark" : "light";
        setThemeState(newTheme);
        applyTheme(newTheme);
      }
      if (e.key === "reducedMotion") {
        const v = e.newValue === "true";
        setReducedMotionState(v);
        applyReducedMotion(v);
      }
      if (e.key === "highContrast") {
        const v = e.newValue === "true";
        setHighContrastState(v);
        applyHighContrast(v);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      colorSchemeQuery.removeEventListener("change", handleColorSchemeChange);
    };
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  const toggleTheme = useCallback(() => {
    const next: Theme = theme === "light" ? "dark" : "light";
    setThemeState(next);
    localStorage.setItem("theme", next);
    applyTheme(next);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem("theme", t);
    applyTheme(t);
  }, []);

  const setReducedMotion = useCallback((v: boolean) => {
    setReducedMotionState(v);
    localStorage.setItem("reducedMotion", String(v));
    applyReducedMotion(v);
  }, []);

  const setHighContrast = useCallback((v: boolean) => {
    setHighContrastState(v);
    localStorage.setItem("highContrast", String(v));
    applyHighContrast(v);
  }, []);

  return (
    <ThemeContext.Provider value={{
      theme,
      reducedMotion,
      highContrast,
      mounted,
      toggleTheme,
      setTheme,
      setReducedMotion,
      setHighContrast,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
